import React, { useState, useEffect } from 'react';
import { ICONS } from '../constants';
import Button from './ui/Button';
import { useTranslation } from '../hooks/useTranslation';
import { API_URL } from '../config';

interface InviteModalProps {
  onClose: () => void;
}

const InviteModal: React.FC<InviteModalProps> = ({ onClose }) => {
  const { t, language } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string>('');

  // Create invite on mount
  useEffect(() => {
    createInvite();
  }, []);

  const createInvite = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('spellbook_token');
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      console.log('Creating invite with token:', token ? 'Token exists' : 'No token');
      
      const response = await fetch(`${API_URL}/api/v1/invite/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          language: language, // Send current language
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create invite');
      }

      const data = await response.json();
      setInviteUrl(data.url);
      setExpiresAt(data.expiresAt);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invite');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!inviteUrl) return;
    
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      try {
        const textarea = document.createElement('textarea');
        textarea.value = inviteUrl;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-hidden">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full min-w-0 p-6 relative overflow-x-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <ICONS.X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="flex items-center space-x-3 mb-6">
          <ICONS.ExternalLink className="w-8 h-8 text-purple-500" />
          <h2 className="text-2xl font-bold text-white">
            {t('invite.modalTitle')}
          </h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-900/30 border border-red-600/50 rounded p-4 mb-4">
            <div className="flex items-center space-x-2">
              <ICONS.AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-200">{error}</p>
            </div>
            <Button
              onClick={createInvite}
              className="mt-4 !bg-red-600 hover:!bg-red-700"
            >
              {t('invite.tryAgain')}
            </Button>
          </div>
        ) : (
          <>
            {/* Success message */}
            <div className="bg-green-900/30 border border-green-600/50 rounded p-4 mb-6">
              <div className="flex items-center space-x-2">
                <ICONS.Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-green-200">
                    {t('invite.linkCreated')}
                  </p>
                  <p className="text-xs text-green-300 mt-1">
                    {t('invite.validFor24Hours')}
                  </p>
                </div>
              </div>
            </div>

            {/* Invite URL */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('invite.inviteLink')}
              </label>
              <div className="flex items-center space-x-2 min-w-0">
                <input
                  type="text"
                  value={inviteUrl}
                  readOnly
                  className="flex-1 min-w-0 max-w-full box-border bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white text-sm font-mono truncate"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <Button
                  onClick={copyToClipboard}
                  className={`!px-4 !py-3 flex items-center ${copied ? '!bg-green-600' : '!bg-indigo-600 hover:!bg-indigo-700'}`}
                  title={t('invite.copyLink')}
                >
                  {copied ? (
                    <>
                      <ICONS.Check className="w-5 h-5" />
                      <span className="hidden sm:inline ml-2">{t('invite.copied')}</span>
                    </>
                  ) : (
                    <>
                      <ICONS.Copy className="w-5 h-5" />
                      <span className="hidden sm:inline ml-2">{t('invite.copy')}</span>
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Expiry info */}
            <div className="bg-gray-700/50 rounded p-4 mb-6">
              <div className="flex items-start space-x-2">
                <ICONS.Clock className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-300">
                  <p className="font-semibold mb-1">{t('invite.expiresAt')}</p>
                  <p className="text-gray-400">
                    {new Date(expiresAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-gray-700/30 rounded p-4 mb-6">
              <p className="text-sm font-semibold text-gray-300 mb-2">
                {t('invite.instructions')}
              </p>
              <ul className="text-sm text-gray-400 space-y-1 list-disc list-inside">
                <li>{t('invite.instruction1')}</li>
                <li>{t('invite.instruction2')}</li>
                <li>{t('invite.instruction3')}</li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <Button
                onClick={onClose}
                className="!bg-gray-700 hover:!bg-gray-600"
              >
                {t('invite.close')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InviteModal;

